import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./MarketingStates.css";
import nigeriaData from "./MARKETTING_DATA";

/* =========================================================
   CONSTANTS
========================================================= */

const QUICK_FILTERS = [
    { id: "all", label: "All" },
    { id: "phone", label: "Has phone" },
    { id: "no-phone", label: "No phone" },
    { id: "website", label: "Has website" },
    { id: "no-website", label: "No website" },
    { id: "email", label: "Has email" },
    { id: "no-email", label: "No email" },
    { id: "address", label: "Has address" },
    { id: "no-address", label: "No address" },
    { id: "duplicates", label: "Duplicate phones" },
];

const FILTER_FIELDS = [
    { id: "country", label: "Country" },
    { id: "state", label: "State" },
    { id: "city", label: "City" },
    { id: "category", label: "Business category" },
    { id: "phone", label: "Phone" },
    { id: "website", label: "Website" },
    { id: "email", label: "Email" },
    { id: "address", label: "Address" },
];

const OPERATORS = [
    { id: "is", label: "is" },
    { id: "is-not", label: "is not" },
    { id: "contains", label: "contains" },
    { id: "not-contains", label: "does not contain" },
];

const createId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/* =========================================================
   GENERIC HELPERS
========================================================= */

const cleanValue = (value) => {
    if (value === null || value === undefined) return "";

    if (Array.isArray(value)) {
        return value.map(cleanValue).filter(Boolean).join(", ");
    }

    if (typeof value === "object") {
        return Object.values(value).map(cleanValue).filter(Boolean).join(", ");
    }

    return String(value).trim();
};

const hasValue = (value) => cleanValue(value).length > 0;

const normalizeText = (value) =>
    cleanValue(value)
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

const normalizePhone = (value) => {
    let phone = cleanValue(value);

    if (!phone) return "";

    phone = phone.replace(/[^\d+]/g, "");

    /*
     * Nigerian number normalization.
     *
     * 08012345678
     * 2348012345678
     * +2348012345678
     *
     * can all resolve to:
     *
     * 2348012345678
     */

    if (phone.startsWith("+")) {
        phone = phone.substring(1);
    }

    if (phone.startsWith("00234")) {
        phone = phone.substring(2);
    }

    if (phone.startsWith("0") && phone.length === 11) {
        phone = `234${phone.substring(1)}`;
    }

    return phone;
};

const isGoogleMapsUrl = (url) => {
    const value = normalizeText(url);

    return (
        value.includes("google.com/maps") ||
        value.includes("maps.google.com") ||
        value.includes("goo.gl/maps")
    );
};

const isWebsite = (business) => {
    /*
     * IMPORTANT:
     * "Place URL" is Google Maps and should NOT count
     * as a business website.
     */

    const possibleWebsiteFields = [
        "Website",
        "website",
        "Website URL",
        "websiteUrl",
        "Business Website",
        "Business Website URL",
        "URL",
    ];

    for (const field of possibleWebsiteFields) {
        const value = cleanValue(business?.[field]);

        if (value && !isGoogleMapsUrl(value)) {
            return true;
        }
    }

    return false;
};

const getWebsite = (business) => {
    const possibleWebsiteFields = [
        "Website",
        "website",
        "Website URL",
        "websiteUrl",
        "Business Website",
        "Business Website URL",
        "URL",
    ];

    for (const field of possibleWebsiteFields) {
        const value = cleanValue(business?.[field]);

        if (value && !isGoogleMapsUrl(value)) {
            return value;
        }
    }

    return "";
};

const getEmail = (business) => {
    const possibleFields = [
        "Email",
        "email",
        "Email Address",
        "emailAddress",
        "Business Email",
        "Business Email Address",
    ];

    for (const field of possibleFields) {
        const value = cleanValue(business?.[field]);

        if (value) return value;
    }

    return "";
};

const getPhone = (business) => {
    const possibleFields = [
        "Phone Number",
        "Phone",
        "phone",
        "Telephone",
        "telephone",
        "Contact Number",
        "contactNumber",
        "Mobile",
        "mobile",
    ];

    for (const field of possibleFields) {
        const value = cleanValue(business?.[field]);

        if (value) return value;
    }

    return "";
};

const getAddress = (business) => {
    const possibleFields = [
        "Address",
        "address",
        "Full Address",
        "fullAddress",
        "Business Address",
    ];

    for (const field of possibleFields) {
        const value = cleanValue(business?.[field]);

        if (value) return value;
    }

    return "";
};

/* =========================================================
   FLATTEN DATA
========================================================= */

const flattenNigeriaData = (source) => {
    if (!Array.isArray(source)) return [];

    const records = [];

    source.forEach((stateEntry, stateIndex) => {
        const state = cleanValue(stateEntry?.state);
        const city = cleanValue(stateEntry?.city);

        const categories = Array.isArray(stateEntry?.data)
            ? stateEntry.data
            : [];

        categories.forEach((categoryEntry, categoryIndex) => {
            const parentCategory = cleanValue(categoryEntry?.businessCategory);

            const slug = cleanValue(categoryEntry?.slug);

            const businesses = Array.isArray(categoryEntry?.businesses)
                ? categoryEntry.businesses
                : [];

            businesses.forEach((business, businessIndex) => {
                const category =
                    cleanValue(business?.["Business Category"]) ||
                    parentCategory;

                const phone = getPhone(business);
                const normalizedPhone = normalizePhone(phone);

                const website = getWebsite(business);
                const email = getEmail(business);
                const address = getAddress(business);

                records.push({
                    id:
                        business?.id ||
                        business?._id ||
                        `${stateIndex}-${categoryIndex}-${businessIndex}`,

                    state,
                    city,
                    country: "Nigeria",

                    slug,
                    businessCategory: category,

                    business,

                    businessName:
                        cleanValue(business?.["Business Name"]) ||
                        cleanValue(business?.businessName) ||
                        "Unnamed business",

                    placeUrl: cleanValue(business?.["Place URL"]),

                    phone,
                    normalizedPhone,

                    website,
                    email,
                    address,

                    rating: Number(
                        business?.["Rating (Max 5)"] ?? business?.rating ?? 0,
                    ),

                    reviews: Number(
                        business?.["Reviews Count"] ?? business?.reviews ?? 0,
                    ),
                });
            });
        });
    });

    return records;
};

/* =========================================================
   ICON COMPONENT
========================================================= */

const Icon = ({ name, size = 18 }) => {
    const icons = {
        search: "⌕",
        filter: "⚙",
        close: "×",
        copy: "▣",
        check: "✓",
        chevron: "⌄",
        external: "↗",
        duplicate: "⚠",
        phone: "☎",
        website: "◉",
        email: "✉",
        location: "⌖",
        star: "★",
        columns: "☷",
        plus: "+",
        trash: "⌫",
    };

    return (
        <span
            aria-hidden="true"
            style={{
                fontSize: size,
                lineHeight: 1,
            }}
        >
            {icons[name] || "•"}
        </span>
    );
};

/* =========================================================
   COPY BUTTON
========================================================= */

const CopyButton = ({ value, label = "Copy", icon = "copy" }) => {
    const [copied, setCopied] = useState(false);

    const copy = async (event) => {
        event.stopPropagation();

        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);

            setCopied(true);

            window.setTimeout(() => {
                setCopied(false);
            }, 1600);
        } catch {
            /*
             * Fallback for browsers where Clipboard API
             * is unavailable.
             */
            const textarea = document.createElement("textarea");

            textarea.value = value;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";

            document.body.appendChild(textarea);
            textarea.select();

            try {
                document.execCommand("copy");
                setCopied(true);

                window.setTimeout(() => {
                    setCopied(false);
                }, 1600);
            } finally {
                document.body.removeChild(textarea);
            }
        }
    };

    if (!value) return null;

    return (
        <button
            type="button"
            className="mi-copy-button"
            onClick={copy}
            title={label}
        >
            <Icon name={copied ? "check" : icon} size={14} />
            {copied ? "Copied" : label}
        </button>
    );
};

/* =========================================================
   STAT CARD
========================================================= */

const StatCard = ({ label, value, description }) => {
    return (
        <div className="mi-stat-card">
            <div className="mi-stat-card-top">
                <span>{label}</span>
            </div>

            <strong>{value.toLocaleString()}</strong>

            {description && <small>{description}</small>}
        </div>
    );
};

/* =========================================================
   FILTER CHIP
========================================================= */

const FilterChip = ({ filter, onRemove }) => {
    return (
        <div className="mi-filter-chip">
            <span className="mi-filter-chip-field">{filter.fieldLabel}</span>

            <span className="mi-filter-chip-operator">
                {filter.operatorLabel}
            </span>

            <strong>{filter.value}</strong>

            <button
                type="button"
                onClick={() => onRemove(filter.id)}
                aria-label="Remove filter"
            >
                ×
            </button>
        </div>
    );
};

/* =========================================================
   ADVANCED FILTER BUILDER
========================================================= */

const AdvancedFilterBuilder = ({ records, filters, setFilters, onClose }) => {
    const [field, setField] = useState("state");
    const [operator, setOperator] = useState("is");
    const [value, setValue] = useState("");

    const options = useMemo(() => {
        const values = new Set();

        records.forEach((record) => {
            let current = "";

            switch (field) {
                case "country":
                    current = record.country;
                    break;

                case "state":
                    current = record.state;
                    break;

                case "city":
                    current = record.city;
                    break;

                case "category":
                    current = record.businessCategory;
                    break;

                case "phone":
                    current = record.phone;
                    break;

                case "website":
                    current = record.website;
                    break;

                case "email":
                    current = record.email;
                    break;

                case "address":
                    current = record.address;
                    break;

                default:
                    break;
            }

            if (hasValue(current)) {
                values.add(current);
            }
        });

        return [...values].sort((a, b) => a.localeCompare(b));
    }, [records, field]);

    const addFilter = () => {
        if (!value.trim()) return;

        const fieldDefinition = FILTER_FIELDS.find((item) => item.id === field);

        const operatorDefinition = OPERATORS.find(
            (item) => item.id === operator,
        );

        setFilters((previous) => [
            ...previous,
            {
                id: createId(),
                field,
                fieldLabel: fieldDefinition?.label || field,
                operator,
                operatorLabel: operatorDefinition?.label || operator,
                value: value.trim(),
            },
        ]);

        setValue("");
    };

    return (
        <div className="mi-filter-builder">
            <div className="mi-filter-builder-header">
                <div>
                    <strong>Advanced filters</strong>
                    <span>
                        Build precise queries across your business data.
                    </span>
                </div>

                <button
                    type="button"
                    className="mi-icon-button"
                    onClick={onClose}
                >
                    <Icon name="close" />
                </button>
            </div>

            <div className="mi-filter-builder-row">
                <select
                    value={field}
                    onChange={(event) => {
                        setField(event.target.value);
                        setValue("");
                    }}
                >
                    {FILTER_FIELDS.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.label}
                        </option>
                    ))}
                </select>

                <select
                    value={operator}
                    onChange={(event) => setOperator(event.target.value)}
                >
                    {OPERATORS.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.label}
                        </option>
                    ))}
                </select>

                <div className="mi-filter-value-wrapper">
                    <input
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        list={`filter-values-${field}`}
                        placeholder="Enter a value..."
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                addFilter();
                            }
                        }}
                    />

                    {options.length > 0 && (
                        <datalist id={`filter-values-${field}`}>
                            {options.map((item) => (
                                <option key={item} value={item} />
                            ))}
                        </datalist>
                    )}
                </div>

                <button
                    type="button"
                    className="mi-primary-button"
                    onClick={addFilter}
                >
                    <Icon name="plus" size={15} />
                    Add filter
                </button>
            </div>

            {filters.length > 0 && (
                <div className="mi-filter-list">
                    {filters.map((filter, index) => (
                        <React.Fragment key={filter.id}>
                            {index > 0 && (
                                <span className="mi-filter-and">AND</span>
                            )}

                            <FilterChip
                                filter={filter}
                                onRemove={(id) =>
                                    setFilters((previous) =>
                                        previous.filter(
                                            (item) => item.id !== id,
                                        ),
                                    )
                                }
                            />
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

/* =========================================================
   DETAIL MODAL
========================================================= */

const BusinessDetailModal = ({
    record,
    duplicateCount,
    duplicateRecords,
    onClose,
    onSelectDuplicate,
}) => {
    if (!record) return null;

    const business = record.business;

    const allFields = Object.entries(business || {});

    return (
        <div
            className="mi-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="mi-detail-modal" role="dialog" aria-modal="true">
                <div className="mi-modal-header">
                    <div className="mi-modal-title-area">
                        <div className="mi-business-avatar">
                            {record.businessName.charAt(0).toUpperCase()}
                        </div>

                        <div>
                            <h2>{record.businessName}</h2>

                            <div className="mi-modal-subtitle">
                                <span>
                                    {record.businessCategory || "Business"}
                                </span>

                                <span>•</span>

                                <span>
                                    {record.city || record.state || "Nigeria"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="mi-icon-button"
                        onClick={onClose}
                    >
                        <Icon name="close" size={24} />
                    </button>
                </div>

                <div className="mi-modal-body">
                    {duplicateCount > 1 && (
                        <div className="mi-duplicate-alert">
                            <div className="mi-duplicate-icon">
                                <Icon name="duplicate" />
                            </div>

                            <div>
                                <strong>Duplicate phone number</strong>

                                <p>
                                    This phone number appears in{" "}
                                    <strong>{duplicateCount}</strong> records in
                                    the collection.
                                </p>
                            </div>
                        </div>
                    )}

                    <section className="mi-detail-section">
                        <div className="mi-section-heading">
                            <div>
                                <h3>Business information</h3>
                                <span>Core business details</span>
                            </div>
                        </div>

                        <div className="mi-detail-grid">
                            <DetailField
                                label="Business name"
                                value={record.businessName}
                                copy
                            />

                            <DetailField
                                label="Business category"
                                value={record.businessCategory}
                                copy
                            />

                            <DetailField label="State" value={record.state} />

                            <DetailField label="City" value={record.city} />

                            <DetailField
                                label="Country"
                                value={record.country}
                            />

                            <DetailField label="Slug" value={record.slug} />
                        </div>
                    </section>

                    <section className="mi-detail-section">
                        <div className="mi-section-heading">
                            <div>
                                <h3>Contact information</h3>
                                <span>Available contact channels</span>
                            </div>
                        </div>

                        <div className="mi-contact-list">
                            <ContactRow
                                icon="phone"
                                label="Phone"
                                value={record.phone}
                                copy
                            />

                            <ContactRow
                                icon="website"
                                label="Website"
                                value={record.website}
                                copy
                                external
                            />

                            <ContactRow
                                icon="email"
                                label="Email"
                                value={record.email}
                                copy
                            />

                            <ContactRow
                                icon="location"
                                label="Address"
                                value={record.address}
                                copy
                            />

                            <ContactRow
                                icon="location"
                                label="Google Maps"
                                value={record.placeUrl}
                                copy
                                external
                            />
                        </div>
                    </section>

                    <section className="mi-detail-section">
                        <div className="mi-section-heading">
                            <div>
                                <h3>Business performance</h3>
                                <span>Public rating information</span>
                            </div>
                        </div>

                        <div className="mi-performance-grid">
                            <div className="mi-performance-card">
                                <span>Rating</span>

                                <strong>
                                    <Icon name="star" size={15} />{" "}
                                    {record.rating || "—"}
                                </strong>
                            </div>

                            <div className="mi-performance-card">
                                <span>Reviews</span>

                                <strong>
                                    {record.reviews
                                        ? record.reviews.toLocaleString()
                                        : "—"}
                                </strong>
                            </div>
                        </div>
                    </section>

                    {duplicateCount > 1 && (
                        <section className="mi-detail-section">
                            <div className="mi-section-heading">
                                <div>
                                    <h3>Other records using this phone</h3>

                                    <span>Duplicate phone group</span>
                                </div>
                            </div>

                            <div className="mi-duplicate-list">
                                {duplicateRecords
                                    .filter((item) => item.id !== record.id)
                                    .map((item) => (
                                        <button
                                            type="button"
                                            key={item.id}
                                            className="mi-duplicate-record"
                                            onClick={() =>
                                                onSelectDuplicate(item)
                                            }
                                        >
                                            <div>
                                                <strong>
                                                    {item.businessName}
                                                </strong>

                                                <span>
                                                    {item.state}
                                                    {" · "}
                                                    {item.businessCategory}
                                                </span>
                                            </div>

                                            <Icon name="chevron" />
                                        </button>
                                    ))}
                            </div>
                        </section>
                    )}

                    <section className="mi-detail-section">
                        <div className="mi-section-heading">
                            <div>
                                <h3>Raw data</h3>
                                <span>
                                    Fields supplied by the source collection
                                </span>
                            </div>
                        </div>

                        <div className="mi-raw-fields">
                            {allFields.map(([key, value]) => (
                                <div className="mi-raw-field" key={key}>
                                    <span>{key}</span>
                                    <strong>{cleanValue(value) || "—"}</strong>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="mi-modal-footer">
                    {record.placeUrl && (
                        <a
                            href={record.placeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mi-secondary-button"
                        >
                            <Icon name="external" size={15} />
                            Open in Google Maps
                        </a>
                    )}

                    <button
                        type="button"
                        className="mi-primary-button"
                        onClick={onClose}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   DETAIL FIELD
========================================================= */

const DetailField = ({ label, value, copy = false }) => {
    return (
        <div className="mi-detail-field">
            <span>{label}</span>

            <div>
                <strong>{value || "—"}</strong>

                {copy && value && <CopyButton value={value} label="Copy" />}
            </div>
        </div>
    );
};

/* =========================================================
   CONTACT ROW
========================================================= */

const ContactRow = ({ icon, label, value, copy, external }) => {
    return (
        <div className="mi-contact-row">
            <div className="mi-contact-icon">
                <Icon name={icon} size={16} />
            </div>

            <div className="mi-contact-content">
                <span>{label}</span>

                {value ? (
                    external ? (
                        <a href={value} target="_blank" rel="noreferrer">
                            {value}
                        </a>
                    ) : (
                        <strong>{value}</strong>
                    )
                ) : (
                    <em>Not available</em>
                )}
            </div>

            <div className="mi-contact-actions">
                {copy && value && <CopyButton value={value} label="Copy" />}
            </div>
        </div>
    );
};

/* =========================================================
   BULK ACTION BAR
========================================================= */

const BulkActionBar = ({
    selectedCount,
    onCopyPhones,
    onCopyEmails,
    onCopyWebsites,
    onClear,
}) => {
    const [status, setStatus] = useState("");

    const performCopy = async (values, type) => {
        const cleanValues = values.map(cleanValue).filter(Boolean);

        if (!cleanValues.length) {
            setStatus(`No ${type} available`);
            return;
        }

        const uniqueValues = [...new Set(cleanValues)];

        const limited =
            type === "phone" ? uniqueValues.slice(0, 100) : uniqueValues;

        try {
            await navigator.clipboard.writeText(limited.join("\n"));

            setStatus(
                `${limited.length.toLocaleString()} ${type}${
                    limited.length === 1 ? "" : "s"
                } copied`,
            );

            window.setTimeout(() => setStatus(""), 2200);
        } catch {
            setStatus("Unable to copy");
        }
    };

    return (
        <div className="mi-bulk-bar">
            <div className="mi-bulk-selection">
                <div className="mi-selected-indicator">
                    <Icon name="check" size={14} />
                </div>

                <strong>{selectedCount.toLocaleString()}</strong>

                <span>selected</span>
            </div>

            <div className="mi-bulk-actions">
                <button
                    type="button"
                    className="mi-secondary-button"
                    onClick={onCopyPhones}
                >
                    <Icon name="phone" size={14} />
                    Copy phones
                </button>

                <button
                    type="button"
                    className="mi-secondary-button"
                    onClick={onCopyEmails}
                >
                    <Icon name="email" size={14} />
                    Copy emails
                </button>

                <button
                    type="button"
                    className="mi-secondary-button"
                    onClick={onCopyWebsites}
                >
                    <Icon name="website" size={14} />
                    Copy websites
                </button>

                <button
                    type="button"
                    className="mi-clear-selection"
                    onClick={onClear}
                >
                    Clear selection
                </button>
            </div>

            {status && (
                <div className="mi-copy-status">
                    <Icon name="check" size={14} />
                    {status}
                </div>
            )}
        </div>
    );
};

/* =========================================================
   MARKETING STATES
========================================================= */

const MarketingStates = ({ data = nigeriaData, pageSize = 25 }) => {
    const [search, setSearch] = useState("");
    const [quickFilter, setQuickFilter] = useState("all");

    const [filters, setFilters] = useState([]);

    const [filterOpen, setFilterOpen] = useState(false);

    const [selectedIds, setSelectedIds] = useState(new Set());

    const [selectedRecord, setSelectedRecord] = useState(null);

    const [sort, setSort] = useState({
        field: "businessName",
        direction: "asc",
    });

    const [page, setPage] = useState(1);

    const [columns, setColumns] = useState({
        business: true,
        category: true,
        phone: true,
        website: true,
        location: true,
        rating: true,
        reviews: true,
    });

    const [columnMenuOpen, setColumnMenuOpen] = useState(false);

    /* -------------------------------------------------------
       NORMALIZE / FLATTEN
    ------------------------------------------------------- */

    const records = useMemo(() => flattenNigeriaData(data), [data]);

    /* -------------------------------------------------------
       DUPLICATE PHONE INDEX
    ------------------------------------------------------- */

    const duplicatePhoneMap = useMemo(() => {
        const map = new Map();

        records.forEach((record) => {
            if (!record.normalizedPhone) return;

            if (!map.has(record.normalizedPhone)) {
                map.set(record.normalizedPhone, []);
            }

            map.get(record.normalizedPhone).push(record);
        });

        return map;
    }, [records]);

    const getDuplicateCount = useCallback(
        (record) => {
            if (!record.normalizedPhone) return 0;

            return duplicatePhoneMap.get(record.normalizedPhone)?.length || 0;
        },
        [duplicatePhoneMap],
    );

    /* -------------------------------------------------------
       GLOBAL STATISTICS
    ------------------------------------------------------- */

    const statistics = useMemo(() => {
        const phones = records.filter((record) => hasValue(record.phone));

        const websites = records.filter((record) => hasValue(record.website));

        const emails = records.filter((record) => hasValue(record.email));

        const addresses = records.filter((record) => hasValue(record.address));

        const duplicates = records.filter(
            (record) => getDuplicateCount(record) > 1,
        );

        return {
            total: records.length,
            phones: phones.length,
            websites: websites.length,
            emails: emails.length,
            addresses: addresses.length,
            duplicates: duplicates.length,
        };
    }, [records, getDuplicateCount]);

    /* -------------------------------------------------------
       QUICK FILTER COUNTS
    ------------------------------------------------------- */

    const quickCounts = useMemo(() => {
        return {
            all: records.length,

            phone: records.filter((record) => hasValue(record.phone)).length,

            "no-phone": records.filter((record) => !hasValue(record.phone))
                .length,

            website: records.filter((record) => hasValue(record.website))
                .length,

            "no-website": records.filter((record) => !hasValue(record.website))
                .length,

            email: records.filter((record) => hasValue(record.email)).length,

            "no-email": records.filter((record) => !hasValue(record.email))
                .length,

            address: records.filter((record) => hasValue(record.address))
                .length,

            "no-address": records.filter((record) => !hasValue(record.address))
                .length,

            duplicates: records.filter(
                (record) => getDuplicateCount(record) > 1,
            ).length,
        };
    }, [records, getDuplicateCount]);

    /* -------------------------------------------------------
       SEARCH + FILTER
    ------------------------------------------------------- */

    const filteredRecords = useMemo(() => {
        let result = [...records];

        const searchValue = normalizeText(search);

        /* Global search */

        if (searchValue) {
            result = result.filter((record) => {
                const searchable = [
                    record.businessName,
                    record.businessCategory,
                    record.state,
                    record.city,
                    record.country,
                    record.phone,
                    record.website,
                    record.email,
                    record.address,
                    record.slug,
                    record.placeUrl,
                ]
                    .map(normalizeText)
                    .join(" ");

                return searchable.includes(searchValue);
            });
        }

        /* Quick filters */

        switch (quickFilter) {
            case "phone":
                result = result.filter((record) => hasValue(record.phone));
                break;

            case "no-phone":
                result = result.filter((record) => !hasValue(record.phone));
                break;

            case "website":
                result = result.filter((record) => hasValue(record.website));
                break;

            case "no-website":
                result = result.filter((record) => !hasValue(record.website));
                break;

            case "email":
                result = result.filter((record) => hasValue(record.email));
                break;

            case "no-email":
                result = result.filter((record) => !hasValue(record.email));
                break;

            case "address":
                result = result.filter((record) => hasValue(record.address));
                break;

            case "no-address":
                result = result.filter((record) => !hasValue(record.address));
                break;

            case "duplicates":
                result = result.filter(
                    (record) => getDuplicateCount(record) > 1,
                );
                break;

            default:
                break;
        }

        /* Advanced filters */

        if (filters.length) {
            result = result.filter((record) =>
                filters.every((filter) => {
                    let current = "";

                    switch (filter.field) {
                        case "country":
                            current = record.country;
                            break;

                        case "state":
                            current = record.state;
                            break;

                        case "city":
                            current = record.city;
                            break;

                        case "category":
                            current = record.businessCategory;
                            break;

                        case "phone":
                            current = record.phone;
                            break;

                        case "website":
                            current = record.website;
                            break;

                        case "email":
                            current = record.email;
                            break;

                        case "address":
                            current = record.address;
                            break;

                        default:
                            break;
                    }

                    const currentValue = normalizeText(current);

                    const targetValue = normalizeText(filter.value);

                    switch (filter.operator) {
                        case "is":
                            return currentValue === targetValue;

                        case "is-not":
                            return currentValue !== targetValue;

                        case "contains":
                            return currentValue.includes(targetValue);

                        case "not-contains":
                            return !currentValue.includes(targetValue);

                        default:
                            return true;
                    }
                }),
            );
        }

        /* Sorting */

        result.sort((a, b) => {
            let first;
            let second;

            switch (sort.field) {
                case "businessName":
                    first = a.businessName;
                    second = b.businessName;
                    break;

                case "category":
                    first = a.businessCategory;
                    second = b.businessCategory;
                    break;

                case "state":
                    first = a.state;
                    second = b.state;
                    break;

                case "city":
                    first = a.city;
                    second = b.city;
                    break;

                case "rating":
                    first = a.rating;
                    second = b.rating;
                    break;

                case "reviews":
                    first = a.reviews;
                    second = b.reviews;
                    break;

                default:
                    first = a.businessName;
                    second = b.businessName;
            }

            if (typeof first === "number" && typeof second === "number") {
                return sort.direction === "asc"
                    ? first - second
                    : second - first;
            }

            return (
                normalizeText(first).localeCompare(normalizeText(second)) *
                (sort.direction === "asc" ? 1 : -1)
            );
        });

        return result;
    }, [records, search, quickFilter, filters, sort, getDuplicateCount]);

    /* -------------------------------------------------------
       PAGINATION
    ------------------------------------------------------- */

    const totalPages = Math.max(
        1,
        Math.ceil(filteredRecords.length / pageSize),
    );

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    useEffect(() => {
        setPage(1);
    }, [search, quickFilter, filters, sort]);

    const visibleRecords = useMemo(() => {
        const start = (page - 1) * pageSize;

        return filteredRecords.slice(start, start + pageSize);
    }, [filteredRecords, page, pageSize]);

    /* -------------------------------------------------------
       SELECTION
    ------------------------------------------------------- */

    const selectedRecords = useMemo(() => {
        const ids = selectedIds;

        return records.filter((record) => ids.has(record.id));
    }, [records, selectedIds]);

    const allVisibleSelected =
        visibleRecords.length > 0 &&
        visibleRecords.every((record) => selectedIds.has(record.id));

    const toggleRecord = (id) => {
        setSelectedIds((previous) => {
            const next = new Set(previous);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    };

    const toggleVisible = () => {
        setSelectedIds((previous) => {
            const next = new Set(previous);

            if (allVisibleSelected) {
                visibleRecords.forEach((record) => next.delete(record.id));
            } else {
                visibleRecords.forEach((record) => next.add(record.id));
            }

            return next;
        });
    };

    const selectAllFiltered = () => {
        setSelectedIds(new Set(filteredRecords.map((record) => record.id)));
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
    };

    /* -------------------------------------------------------
       BULK COPY
    ------------------------------------------------------- */

    const copyValues = async (values, limit = null) => {
        const unique = [...new Set(values.map(cleanValue).filter(Boolean))];

        const output = limit ? unique.slice(0, limit) : unique;

        if (!output.length) return 0;

        await navigator.clipboard.writeText(output.join("\n"));

        return output.length;
    };

    const copySelectedPhones = async () => {
        const phones = selectedRecords.map((record) => record.phone || "");

        return copyValues(phones, 100);
    };

    const copySelectedEmails = async () => {
        return copyValues(selectedRecords.map((record) => record.email));
    };

    const copySelectedWebsites = async () => {
        return copyValues(selectedRecords.map((record) => record.website));
    };

    /* -------------------------------------------------------
       DETAIL DUPLICATES
    ------------------------------------------------------- */

    const duplicateRecords = selectedRecord?.normalizedPhone
        ? duplicatePhoneMap.get(selectedRecord.normalizedPhone) || []
        : [];

    /* -------------------------------------------------------
       SORT
    ------------------------------------------------------- */

    const handleSort = (field) => {
        setSort((previous) => ({
            field,
            direction:
                previous.field === field && previous.direction === "asc"
                    ? "desc"
                    : "asc",
        }));
    };

    /* -------------------------------------------------------
       FILTER REMOVAL
    ------------------------------------------------------- */

    const removeFilter = (id) => {
        setFilters((previous) => previous.filter((filter) => filter.id !== id));
    };

    const clearFilters = () => {
        setFilters([]);
        setQuickFilter("all");
        setSearch("");
    };

    /* -------------------------------------------------------
       RENDER
    ------------------------------------------------------- */

    return (
        <div className="marketing-inventory">
            {/* HEADER */}

            <header className="mi-header">
                <div>
                    <div className="mi-eyebrow">UNIVERSE INVENTORY</div>

                    <h1>Marketing States</h1>

                    <p>
                        Explore, filter and extract business intelligence from
                        your marketing database.
                    </p>
                </div>

                <div className="mi-header-actions">
                    <button
                        type="button"
                        className="mi-secondary-button"
                        onClick={selectAllFiltered}
                    >
                        Select all results
                    </button>

                    {selectedIds.size > 0 && (
                        <button
                            type="button"
                            className="mi-primary-button"
                            onClick={clearSelection}
                        >
                            <Icon name="close" size={14} />
                            Clear selection
                        </button>
                    )}
                </div>
            </header>

            {/* STATS */}

            <div className="mi-stats-grid">
                <StatCard
                    label="Total businesses"
                    value={statistics.total}
                    description="Complete collection"
                />

                <StatCard
                    label="With phone"
                    value={statistics.phones}
                    description={`${(
                        (statistics.phones / Math.max(statistics.total, 1)) *
                        100
                    ).toFixed(1)}% coverage`}
                />

                <StatCard
                    label="With website"
                    value={statistics.websites}
                    description="Business websites only"
                />

                <StatCard
                    label="With email"
                    value={statistics.emails}
                    description="Available contact emails"
                />

                <StatCard
                    label="Duplicates"
                    value={statistics.duplicates}
                    description="Records sharing phones"
                />
            </div>

            {/* SEARCH */}

            <div className="mi-search-panel">
                <div className="mi-search-wrapper">
                    <Icon name="search" size={23} />

                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search businesses, phones, states, cities, categories..."
                    />

                    {search && (
                        <button
                            type="button"
                            className="mi-search-clear"
                            onClick={() => setSearch("")}
                        >
                            <Icon name="close" />
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    className={`mi-filter-button ${
                        filterOpen ? "is-active" : ""
                    }`}
                    onClick={() => setFilterOpen((previous) => !previous)}
                >
                    <Icon name="filter" size={16} />
                    Advanced filters
                    {filters.length > 0 && (
                        <span className="mi-filter-count">
                            {filters.length}
                        </span>
                    )}
                </button>

                <div className="mi-columns-wrapper">
                    <button
                        type="button"
                        className="mi-filter-button"
                        onClick={() =>
                            setColumnMenuOpen((previous) => !previous)
                        }
                    >
                        <Icon name="columns" size={16} />
                        Columns
                    </button>

                    {columnMenuOpen && (
                        <div className="mi-column-menu">
                            {[
                                ["business", "Business"],
                                ["category", "Category"],
                                ["phone", "Phone"],
                                ["website", "Website"],
                                ["location", "Location"],
                                ["rating", "Rating"],
                                ["reviews", "Reviews"],
                            ].map(([key, label]) => (
                                <label key={key} className="mi-column-option">
                                    <input
                                        type="checkbox"
                                        checked={columns[key]}
                                        onChange={() =>
                                            setColumns((previous) => ({
                                                ...previous,
                                                [key]: !previous[key],
                                            }))
                                        }
                                    />

                                    {label}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ADVANCED FILTER BUILDER */}

            {filterOpen && (
                <AdvancedFilterBuilder
                    records={records}
                    filters={filters}
                    setFilters={setFilters}
                    onClose={() => setFilterOpen(false)}
                />
            )}

            {/* ACTIVE FILTERS */}

            {(filters.length > 0 || quickFilter !== "all" || search) && (
                <div className="mi-active-filters">
                    <div className="mi-active-label">Active filters</div>

                    {search && (
                        <div className="mi-filter-chip">
                            <span>Search</span>
                            <strong>{search}</strong>

                            <button type="button" onClick={() => setSearch("")}>
                                ×
                            </button>
                        </div>
                    )}

                    {quickFilter !== "all" && (
                        <div className="mi-filter-chip">
                            <span>Quick filter</span>

                            <strong>
                                {
                                    QUICK_FILTERS.find(
                                        (item) => item.id === quickFilter,
                                    )?.label
                                }
                            </strong>

                            <button
                                type="button"
                                onClick={() => setQuickFilter("all")}
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {filters.map((filter) => (
                        <FilterChip
                            key={filter.id}
                            filter={filter}
                            onRemove={removeFilter}
                        />
                    ))}

                    <button
                        type="button"
                        className="mi-clear-filters"
                        onClick={clearFilters}
                    >
                        Clear all
                    </button>
                </div>
            )}

            {/* QUICK FILTERS */}

            <div className="mi-quick-filters">
                {QUICK_FILTERS.map((filter) => (
                    <button
                        type="button"
                        key={filter.id}
                        className={quickFilter === filter.id ? "is-active" : ""}
                        onClick={() => setQuickFilter(filter.id)}
                    >
                        <span>{filter.label}</span>

                        <strong>
                            {quickCounts[filter.id].toLocaleString()}
                        </strong>
                    </button>
                ))}
            </div>

            {/* RESULT TOOLBAR */}

            <div className="mi-results-toolbar">
                <div>
                    <strong>{filteredRecords.length.toLocaleString()}</strong>

                    <span> matching businesses</span>
                </div>

                <div className="mi-toolbar-controls">
                    <label>
                        Sort by
                        <select
                            value={`${sort.field}:${sort.direction}`}
                            onChange={(event) => {
                                const [field, direction] =
                                    event.target.value.split(":");

                                setSort({
                                    field,
                                    direction,
                                });
                            }}
                        >
                            <option value="businessName:asc">
                                Business name A–Z
                            </option>

                            <option value="businessName:desc">
                                Business name Z–A
                            </option>

                            <option value="state:asc">State A–Z</option>

                            <option value="city:asc">City A–Z</option>

                            <option value="rating:desc">Highest rating</option>

                            <option value="reviews:desc">Most reviews</option>
                        </select>
                    </label>
                </div>
            </div>

            {/* BULK ACTIONS */}

            {selectedIds.size > 0 && (
                <BulkActionBar
                    selectedCount={selectedIds.size}
                    onCopyPhones={async () => {
                        await copySelectedPhones();
                    }}
                    onCopyEmails={async () => {
                        await copySelectedEmails();
                    }}
                    onCopyWebsites={async () => {
                        await copySelectedWebsites();
                    }}
                    onClear={clearSelection}
                />
            )}

            {/* TABLE */}

            <div className="mi-table-shell">
                <div className="mi-table-scroll">
                    <table className="mi-table">
                        <thead>
                            <tr>
                                <th className="mi-checkbox-column">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={toggleVisible}
                                        aria-label="Select visible records"
                                    />
                                </th>

                                {columns.business && (
                                    <th
                                        onClick={() =>
                                            handleSort("businessName")
                                        }
                                    >
                                        Business
                                    </th>
                                )}

                                {columns.category && (
                                    <th onClick={() => handleSort("category")}>
                                        Category
                                    </th>
                                )}

                                {columns.phone && <th>Phone</th>}

                                {columns.website && <th>Website</th>}

                                {columns.location && (
                                    <th onClick={() => handleSort("state")}>
                                        Location
                                    </th>
                                )}

                                {columns.rating && (
                                    <th onClick={() => handleSort("rating")}>
                                        Rating
                                    </th>
                                )}

                                {columns.reviews && (
                                    <th onClick={() => handleSort("reviews")}>
                                        Reviews
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRecords.map((record) => {
                                const duplicateCount =
                                    getDuplicateCount(record);

                                const selected = selectedIds.has(record.id);

                                return (
                                    <tr
                                        key={record.id}
                                        className={
                                            selected ? "is-selected" : ""
                                        }
                                        onClick={() =>
                                            setSelectedRecord(record)
                                        }
                                    >
                                        <td
                                            className="mi-checkbox-column"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() =>
                                                    toggleRecord(record.id)
                                                }
                                                aria-label={`Select ${record.businessName}`}
                                            />
                                        </td>

                                        {columns.business && (
                                            <td>
                                                <div className="mi-business-cell">
                                                    <div className="mi-table-avatar">
                                                        {record.businessName
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>

                                                    <div>
                                                        <strong>
                                                            {
                                                                record.businessName
                                                            }
                                                        </strong>

                                                        <span>
                                                            {record.address ||
                                                                "No address"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        )}

                                        {columns.category && (
                                            <td>
                                                <span className="mi-category-badge">
                                                    {record.businessCategory ||
                                                        "Uncategorized"}
                                                </span>
                                            </td>
                                        )}

                                        {columns.phone && (
                                            <td>
                                                {record.phone ? (
                                                    <div className="mi-phone-cell">
                                                        <span>
                                                            {record.phone}
                                                        </span>

                                                        {duplicateCount > 1 && (
                                                            <span
                                                                className="mi-duplicate-badge"
                                                                title={`Appears ${duplicateCount} times`}
                                                            >
                                                                <Icon
                                                                    name="duplicate"
                                                                    size={12}
                                                                />
                                                                {duplicateCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="mi-empty-value">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                        )}

                                        {columns.website && (
                                            <td>
                                                {record.website ? (
                                                    <div className="mi-url-cell">
                                                        <span>
                                                            {record.website.replace(
                                                                /^https?:\/\//,
                                                                "",
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="mi-empty-value">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                        )}

                                        {columns.location && (
                                            <td>
                                                <div className="mi-location-cell">
                                                    <strong>
                                                        {record.state}
                                                    </strong>

                                                    <span>{record.city}</span>
                                                </div>
                                            </td>
                                        )}

                                        {columns.rating && (
                                            <td>
                                                <span className="mi-rating">
                                                    <Icon
                                                        name="star"
                                                        size={13}
                                                    />

                                                    {record.rating || "—"}
                                                </span>
                                            </td>
                                        )}

                                        {columns.reviews && (
                                            <td>
                                                {record.reviews
                                                    ? record.reviews.toLocaleString()
                                                    : "—"}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {visibleRecords.length === 0 && (
                    <div className="mi-empty-state">
                        <div className="mi-empty-icon">
                            <Icon name="search" size={30} />
                        </div>

                        <h3>No businesses found</h3>

                        <p>No records match your current search and filters.</p>

                        <button
                            type="button"
                            className="mi-primary-button"
                            onClick={clearFilters}
                        >
                            Clear all filters
                        </button>
                    </div>
                )}
            </div>

            {/* PAGINATION */}

            {filteredRecords.length > 0 && (
                <div className="mi-pagination">
                    <div>
                        Showing{" "}
                        <strong>
                            {((page - 1) * pageSize + 1).toLocaleString()}
                        </strong>{" "}
                        –{" "}
                        <strong>
                            {Math.min(
                                page * pageSize,
                                filteredRecords.length,
                            ).toLocaleString()}
                        </strong>{" "}
                        of{" "}
                        <strong>
                            {filteredRecords.length.toLocaleString()}
                        </strong>
                    </div>

                    <div className="mi-pagination-buttons">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((previous) => previous - 1)}
                        >
                            Previous
                        </button>

                        <span>
                            Page {page} of {totalPages}
                        </span>

                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((previous) => previous + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* DETAIL MODAL */}

            {selectedRecord && (
                <BusinessDetailModal
                    record={selectedRecord}
                    duplicateCount={getDuplicateCount(selectedRecord)}
                    duplicateRecords={duplicateRecords}
                    onClose={() => setSelectedRecord(null)}
                    onSelectDuplicate={(record) => setSelectedRecord(record)}
                />
            )}
        </div>
    );
};

export default MarketingStates;
